package top.wcpe.allincore.ui.runtime.event

import top.wcpe.allincore.ui.runtime.element.ScrollableContainer
import top.wcpe.allincore.ui.runtime.element.UIElement
import kotlin.jvm.JvmStatic

/**
 * 拖拽捕获状态管理器。
 * 封装鼠标按下后的"指针捕获"机制：记录被捕获的目标元素及其绝对盒，
 * 在整个 drag 序列中跳过 hit test 直接投递事件，直到 mouseReleased 释放。
 */
internal object DragCaptureManager {

    private const val TRIGGER_RELEASE = "release"

    private var dragCaptureTarget: UIElement<*>? = null
    private var dragCaptureAbsBox: FloatArray? = null
    private var pressedElement: UIElement<*>? = null

    @JvmStatic
    fun getDragCaptureTarget(): UIElement<*>? = dragCaptureTarget

    @JvmStatic
    fun getDragCaptureAbsBox(): FloatArray? = dragCaptureAbsBox

    @JvmStatic
    fun getPressedElement(): UIElement<*>? = pressedElement

    @JvmStatic
    fun setPressedElement(element: UIElement<*>?) {
        pressedElement = element
    }

    /**
     * 若命中路径末端是 Slider，记录为鼠标捕获目标。
     */
    @JvmStatic
    fun captureDragIfSlider(path: List<UIElement<*>>, absBoxes: List<FloatArray>) {
        if (path.isEmpty() || absBoxes.isEmpty()) return
        val tail = path[path.size - 1]
        if (tail !is SliderInputTarget) return
        dragCaptureTarget = tail
        dragCaptureAbsBox = absBoxes[absBoxes.size - 1]
    }

    /**
     * 若命中路径末端是启用的 TextField，记录为捕获目标（仅在 Slider 未捕获时）。
     */
    @JvmStatic
    fun captureDragIfTextField(path: List<UIElement<*>>, absBoxes: List<FloatArray>) {
        if (path.isEmpty() || absBoxes.isEmpty()) return
        if (dragCaptureTarget != null) return
        val tail = path[path.size - 1]
        if (tail !is TextFieldInputTarget || !tail.enabled()) return
        dragCaptureTarget = tail
        dragCaptureAbsBox = absBoxes[absBoxes.size - 1]
    }

    /**
     * 若命中路径里有 ScrollableContainer，记录最深的那一个为捕获目标。
     * 仅在 Slider 没命中时调用；命中链上有 onDragStart 元素时让出 capture。
     */
    @JvmStatic
    fun captureDragIfScrollable(path: List<UIElement<*>>, absBoxes: List<FloatArray>) {
        if (path.isEmpty() || absBoxes.isEmpty()) return
        if (dragCaptureTarget != null) return
        for (el in path) {
            if (el.onDragStartHandler() != null) return
        }
        for (i in path.indices.reversed()) {
            val el = path[i]
            if (el is ScrollableContainer<*>) {
                dragCaptureTarget = el
                dragCaptureAbsBox = absBoxes[i]
                return
            }
        }
    }

    /**
     * 释放鼠标捕获并触发 release 动画。
     * 若捕获目标是 ScrollableContainer，额外通知 onDragEnd 触发惯性/弹性回弹。
     */
    @JvmStatic
    fun releaseDragCapture() {
        (dragCaptureTarget as? ScrollableContainer<*>)?.let { sc ->
            try {
                sc.onDragEnd()
            } catch (_: Throwable) { }
        }
        dragCaptureTarget = null
        dragCaptureAbsBox = null
        val el = pressedElement ?: return
        el.pressed(false)
        // marker 断环，见 AnimationDriverHandle
        el.animationScheduler()?.playTrigger(el, TRIGGER_RELEASE)
        pressedElement = null
    }

    /**
     * 对被捕获的 Slider 应用拖拽交互。
     */
    @JvmStatic
    fun applySliderInteraction(mx: Double): Boolean {
        val target = dragCaptureTarget as? SliderInputTarget ?: return false
        val absBox = dragCaptureAbsBox ?: return false
        val absW = absBox[2]
        if (absW <= 0f) return false
        target.applySliderRatio((mx - absBox[0]) / absW)
        return true
    }
}
